/**
 * "What a strong answer shows" under a recruiter's question: the area it
 * counts toward, and the signals of an excellent, adequate and weak answer,
 * as Rio sketched them. Every line edits in place; the area picker offers
 * only the job's areas; the switch decides whether the answer counts toward
 * the fit score.
 */
import { useId, useState, type KeyboardEvent } from 'react';
import type {
  CustomQuestion,
  ExpectationLevel,
  SkillRef,
} from '../../services/interviewer.types';
import { EXPECTATION_LEVELS } from '../../services/interviewer.types';
import { MAX_SIGNALS, MAX_SIGNAL_LENGTH } from '../../interviewer/model';
import { CloseIcon, PlusIcon } from './icons';
import styles from './index.module.css';

interface Props {
  question: CustomQuestion;
  areas: SkillRef[];
  disabled: boolean;
  /** Rio is still sketching this question's bar. */
  sketching: boolean;
  onArea: (area: SkillRef | null) => void;
  onCounts: (counts: boolean) => void;
  onLevel: (level: ExpectationLevel, items: string[]) => void;
}

const LEVEL_LABELS: Record<ExpectationLevel, string> = {
  excellent: 'Excellent',
  adequate: 'Adequate',
  weak: 'Weak',
};

const cx = (...names: (string | false | undefined)[]) =>
  names.filter(Boolean).join(' ');

const QuestionBar = ({
  question,
  areas,
  disabled,
  sketching,
  onArea,
  onCounts,
  onLevel,
}: Props) => {
  const ids = useId();
  const [editing, setEditing] = useState<{
    level: ExpectationLevel;
    index: number;
  } | null>(null);
  const [draft, setDraft] = useState('');
  const bar = question.expectations;

  const commit = () => {
    if (!editing || !bar) return;
    const items = [...bar[editing.level]];
    const text = draft.trim();
    if (editing.index >= items.length) {
      if (text) items.push(text);
    } else if (text) {
      items[editing.index] = text;
    } else {
      items.splice(editing.index, 1);
    }
    onLevel(editing.level, items);
    setEditing(null);
  };

  const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
    if (event.key === 'Escape') setEditing(null);
  };

  return (
    <div className={styles.bar} aria-busy={sketching}>
      <div className={styles.barHead}>
        <span className={styles.barTitle}>What a strong answer shows</span>
        <span className={styles.barMeta}>
          <label className={styles.srOnly} htmlFor={`${ids}-area`}>
            Counts toward
          </label>
          <select
            id={`${ids}-area`}
            className={styles.select}
            value={question.area ?? ''}
            disabled={disabled || !question.counts}
            onChange={(e) => {
              const area = areas.find((a) => a.key === e.target.value) ?? null;
              onArea(area);
            }}
          >
            <option value="">Choose an area</option>
            {areas.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        </span>
      </div>

      {sketching && !bar && (
        <p className={styles.sketching} role="status">
          <span className={styles.thinking} aria-hidden>
            <span />
            <span />
            <span />
          </span>
          Rio is sketching the bar for this question…
        </p>
      )}

      {bar && (
        <div className={styles.levels}>
          {EXPECTATION_LEVELS.map((level) => (
            <div
              key={level}
              className={cx(styles.level, styles[`level-${level}`])}
            >
              <span className={styles.levelLabel}>{LEVEL_LABELS[level]}</span>
              <ul className={styles.signals}>
                {bar[level].map((item, index) =>
                  editing?.level === level && editing.index === index ? (
                    <li key={`${level}-${index}-edit`}>
                      <input
                        className={styles.signalInput}
                        aria-label={`Edit ${LEVEL_LABELS[level]} signal`}
                        value={draft}
                        maxLength={MAX_SIGNAL_LENGTH}
                        autoFocus
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={onKey}
                      />
                    </li>
                  ) : (
                    <li key={`${level}-${index}`} className={styles.signal}>
                      <button
                        type="button"
                        className={styles.signalText}
                        aria-label={`Edit ${LEVEL_LABELS[level]} signal: ${item}`}
                        disabled={disabled}
                        onClick={() => {
                          setEditing({ level, index });
                          setDraft(item);
                        }}
                      >
                        {item}
                      </button>
                      <button
                        type="button"
                        className={styles.signalRemove}
                        aria-label={`Remove ${LEVEL_LABELS[level]} signal: ${item}`}
                        disabled={disabled}
                        onClick={() =>
                          onLevel(
                            level,
                            bar[level].filter((_, i) => i !== index),
                          )
                        }
                      >
                        <CloseIcon />
                      </button>
                    </li>
                  ),
                )}
                {editing?.level === level &&
                  editing.index === bar[level].length && (
                    <li key={`${level}-new`}>
                      <input
                        className={styles.signalInput}
                        aria-label={`New ${LEVEL_LABELS[level]} signal`}
                        value={draft}
                        maxLength={MAX_SIGNAL_LENGTH}
                        placeholder="What shows it"
                        autoFocus
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={onKey}
                      />
                    </li>
                  )}
              </ul>
              {bar[level].length < MAX_SIGNALS &&
                !(editing?.level === level) && (
                  <button
                    type="button"
                    className={styles.signalAdd}
                    disabled={disabled}
                    onClick={() => {
                      setEditing({ level, index: bar[level].length });
                      setDraft('');
                    }}
                  >
                    <PlusIcon /> Add
                  </button>
                )}
            </div>
          ))}
        </div>
      )}

      <div className={styles.countsRow}>
        <button
          type="button"
          role="switch"
          className={styles.switch}
          aria-checked={question.counts}
          aria-labelledby={`${ids}-counts`}
          disabled={disabled}
          onClick={() => onCounts(!question.counts)}
        />
        <span id={`${ids}-counts`} className={styles.countsLabel}>
          {question.counts
            ? 'Counts toward the fit score'
            : 'For information only'}
        </span>
      </div>
    </div>
  );
};

export default QuestionBar;
